import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import JSZip from 'jszip/dist/jszip';

@Injectable({
  providedIn: 'root',
})
export class ZipDownloadService {
  constructor() { }

  /**
   * YYYYMMDD - seller name - invoice number.
   * Date is the invoice date. A missing date, seller, or invoice number is omitted.
   * Returns an empty string when every part is missing so the stored file name is kept.
   */
  buildInvoiceDownloadName(sellerName?: string, invoiceNo?: string, invoiceDate?: Date | string | null): string {
    const date = this.formatInvoiceDate(invoiceDate);
    const seller = this.sanitizeFileNamePart(sellerName);
    const number = this.sanitizeFileNamePart(invoiceNo);
    return [date, seller, number].filter(part => !!part).join(' - ');
  }

  // Function to download images and PDFs as ZIP
  async downloadFilesAsZip(sources) {
    if (!sources?.length) {
      return;
    }

    const namedSources = this.assignDownloadFileNames(sources);

    if (namedSources.length === 1) {
      await this.downloadFile(namedSources[0].path, namedSources[0].resolvedName, true);
      return;
    }

    const zip = new JSZip();
    const invoiceFolder = zip.folder('invoice');

    const filePromises = namedSources.map(async (file) => {
      try {
        const response = await fetch(decodeURIComponent(file.path));
        const blob = await response.blob();
        invoiceFolder?.file(file.resolvedName, blob);
      } catch (err) {
        console.log('Fail:: download', err.message);
      }
    });

    await Promise.all(filePromises);
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, this.zipFileName(sources));
  }

  async downloadFile(url, name, useExactName = false) {
    try {
      const response = await fetch(decodeURIComponent(url));
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const extension = name.split('.').pop()?.toLowerCase();
      const fileName = useExactName
        ? name
        : `${name.replace(/\.[^/.]+$/, '')}-${new Date().getTime()}.${extension}`;

      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

    } catch (err) {
      console.log('Error downloading::', err.message)
    }
  }

  private assignDownloadFileNames(sources) {
    const usedNames = new Map<string, number>();

    return sources.map((file) => {
      const extension = this.getExtension(file);
      let baseName = file.downloadName;

      if (!baseName) {
        baseName = `${file.name.replace(/\.[^/.]+$/, '')}-${new Date().getTime()}`;
      } else {
        const seen = usedNames.get(baseName) || 0;
        usedNames.set(baseName, seen + 1);
        if (seen > 0) {
          baseName = `${file.downloadName} - ${seen + 1}`;
        }
      }

      return {
        ...file,
        resolvedName: extension ? `${baseName}.${extension}` : baseName
      };
    });
  }

  private zipFileName(sources): string {
    const downloadNames = sources
      .map(source => source.downloadName)
      .filter(name => !!name);
    const uniqueNames = new Set(downloadNames);

    if (downloadNames.length === sources.length && uniqueNames.size === 1) {
      return `${downloadNames[0]}.zip`;
    }

    if (downloadNames.length) {
      return `invoices-${this.currentDateStamp(true)}.zip`;
    }

    return `invoice-${new Date().getTime()}.zip`;
  }

  private formatInvoiceDate(invoiceDate?: Date | string | null): string {
    if (!invoiceDate) {
      return '';
    }

    if (typeof invoiceDate === 'string') {
      const match = invoiceDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return `${match[1]}${match[2]}${match[3]}`;
      }
    }

    const parsed = invoiceDate instanceof Date ? invoiceDate : new Date(invoiceDate);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${parsed.getFullYear()}${pad(parsed.getMonth() + 1)}${pad(parsed.getDate())}`;
  }

  private currentDateStamp(withTime = false): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;

    if (!withTime) {
      return date;
    }

    return `${date}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  }

  private getExtension(file): string {
    const name = file?.name || '';
    const dot = name.lastIndexOf('.');
    if (dot > 0 && dot < name.length - 1) {
      return name.slice(dot + 1).toLowerCase();
    }

    let decodedPath = file?.path || '';
    try {
      decodedPath = decodeURIComponent(decodedPath);
    } catch {
      decodedPath = file?.path || '';
    }
    const pathName = decodedPath.split('?')[0].split('/').pop() || '';
    const pathDot = pathName.lastIndexOf('.');
    if (pathDot > 0 && pathDot < pathName.length - 1) {
      const pathExtension = pathName.slice(pathDot + 1).toLowerCase();
      if (/^[a-z0-9]{1,5}$/.test(pathExtension)) {
        return pathExtension;
      }
    }

    const type = (file?.type || '').toLowerCase();
    if (type.includes('pdf')) return 'pdf';
    if (type.includes('png')) return 'png';
    if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
    if (type.includes('webp')) return 'webp';
    if (type.includes('gif')) return 'gif';
    return '';
  }

  private sanitizeFileNamePart(value?: string): string {
    return (value || '')
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[. ]+$/g, '');
  }

  getFileNameAndType(url: string): { fileName: string; fileType: string } {
    const fileNameWithParams = url.split('/').pop() || '';
    const fileName = fileNameWithParams.split('?')[0];
    const extension = fileName.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];

    let fileType: string;
    if (extension === 'pdf') {
      fileType = 'pdf';
    } else if (imageExtensions.includes(extension || '')) {
      fileType = 'image';
    } else {
      fileType = '';
    }

    return { fileName, fileType };
  }

}
